import { useEffect, useState } from 'react';
import './DownloadPage.css';
import playNoSignUpSVG from '../images/play/play-no-sign-up.svg';
import playWindowsBG from '../images/play/play-windows-bg.webp';
import playJarBG from '../images/play/play-jar-bg.webp';
import playMicrosoftSVG from '../images/play/play-microsoft.svg';
import playJavaLogoSVG from '../images/play/play-java-logo.svg';
import playExeFileSVG from '../images/play/play-exe-file.svg';
import playJarFileSVG from '../images/play/play-jar-file.svg';
import discordIconSVG from '../images/home/discord-icon.svg';
import ReasonNav from '../components/reason/ReasonNav';
import ReasonFooter from '../components/reason/ReasonFooter';
import { SITE_DISCORD } from '../data/siteContact';

const JARFIX_URL = 'https://johann.loefflmann.net/downloads/jarfix.exe';

// GitHub raw URL pattern for downloads (similar to Launcher.java)
// Format: https://github.com/{USER}/{REPO}/raw/refs/heads/main/{VERSION}/{FILE}
const GITHUB_DOWNLOAD_BASE = 'https://github.com/Kezlov/downloads/raw/refs/heads/main/production-latest';

const DOWNLOAD_OPTIONS = [
  {
    id: 'windows-launcher',
    recommended: true,
    bgImage: playWindowsBG,
    logoImage: playMicrosoftSVG,
    fileIcon: playExeFileSVG,
    fileType: '.exe file',
    title: 'Windows Launcher',
    description:
      'This Launcher downloads Java 21 automatically on first run. Just download and play!',
    buttons: [
      {
        label: 'Download .exe',
        href: `${GITHUB_DOWNLOAD_BASE}/TreasonSetup.exe`,
        className: 'button',
      },
    ],
  },
  {
    id: 'launcher-no-java',
    bgImage: playJarBG,
    logoImage: playJavaLogoSVG,
    fileIcon: playJarFileSVG,
    fileType: '.jar file',
    title: 'Launcher without Java 21',
    description: (
      <>
        Please download{' '}
        <a href={JARFIX_URL} target="_blank" rel="noreferrer">
          Jarfix
        </a>{' '}
        if you never ran a java file before. Try this if the other launcher didn&apos;t work or you
        never ran Java before
      </>
    ),
    buttons: [
      {
        label: 'Download .jar',
        href: `${GITHUB_DOWNLOAD_BASE}/Treason.jar`,
        className: 'button',
      },
      {
        label: 'Download Jarfix',
        href: JARFIX_URL,
        className: 'button third',
      },
    ],
  },
  {
    id: 'launcher-all-os',
    bgImage: playJarBG,
    logoImage: playJavaLogoSVG,
    fileIcon: playJarFileSVG,
    fileType: '.jar file',
    title: 'Launcher for all OS',
    description:
      'Our packaged JAR file will work on all Operating Systems, but requires Java 21.',
    buttons: [
      {
        label: 'Download .jar',
        href: `${GITHUB_DOWNLOAD_BASE}/Treason.jar`,
        className: 'button',
      },
    ],
  },
];

const JAVA_DOWNLOADS = [
  {
    label: 'Download Windows JDK 21 installer',
    href: 'https://github.com/adoptium/temurin21-binaries/releases/download/jdk-21.0.5%2B11/OpenJDK21U-jdk_x64_windows_hotspot_21.0.5_11.msi',
  },
  {
    label: 'Download Apple Intel JDK 21 installer',
    href: 'https://github.com/adoptium/temurin21-binaries/releases/download/jdk-21.0.5%2B11/OpenJDK21U-jdk_x64_mac_hotspot_21.0.5_11.pkg',
  },
  {
    label: 'Download Apple M1-M4 JDK 21 installer',
    href: 'https://github.com/adoptium/temurin21-binaries/releases/download/jdk-21.0.5%2B11/OpenJDK21U-jdk_aarch64_mac_hotspot_21.0.5_11.pkg',
  },
];

function DownloadCard({ option }) {
  return (
    <div className="download-box">
      {option.recommended && <div className="recommended-tag">Recommended</div>}
      <img src={option.bgImage} className="play-bg-image" alt="" />
      <div className="icon-container">
        <img src={option.logoImage} className="play-logo-image" alt="" />
      </div>
      <div className="information-panel">
        <div className="file-type">
          <img src={option.fileIcon} alt="" />
          {option.fileType}
        </div>
        <div className="file-name">{option.title}</div>
        <div className="file-description">{option.description}</div>
        {option.buttons.map((button) => (
          <div key={button.label} className="download-actions">
            <a
              href={button.href}
              className={button.className}
              target="_blank"
              rel="noreferrer"
              style={{ width: '100%' }}
            >
              {button.label}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function DownloadPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const root = document.getElementById('root');
    const prevRootBg = root?.style.backgroundColor;
    if (root) root.style.backgroundColor = '#111013';
    return () => {
      document.body.style.overflow = '';
      if (root) root.style.backgroundColor = prevRootBg || '';
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  return (
    <div className={`reason-page play-page${menuOpen ? ' menu-open' : ''}`}>
      <ReasonNav activePage="play" menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="container play-body">
        <div className="recommended">
          <h2>DOWNLOAD &amp; PLAY</h2>
        </div>

        <div className="main-container intro-panel">
          <img src={playNoSignUpSVG} alt="" />
          <div>
            <span>
              Download the client below to start playing. Simply enter a username and password on
              the launcher to create an account—
            </span>
            <span className="intro-highlight">no sign-up required!</span>
            <span className="intro-note"> Note: Some usernames may already be taken.</span>
          </div>
        </div>

        <div className="play-buttons-container">
          {DOWNLOAD_OPTIONS.map((option) => (
            <DownloadCard key={option.id} option={option} />
          ))}
        </div>

        <div className="help-support">
          <h2>HELP &amp; SUPPORT</h2>

          <div className="help-buttons-container">
            <div className="main-container">
              <div className="secondary-title">Download Java 21</div>
              <p className="help-muted">You can install Java 21 from below:</p>
              <div className="help-links">
                {JAVA_DOWNLOADS.map((item) => (
                  <div key={item.label}>
                    <a href={item.href} target="_blank" rel="noreferrer">
                      {item.label}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="main-container">
              <div className="secondary-title">Need additional help?</div>
              <p className="help-muted">Come reach out to us on discord.</p>
              <a
                className="button secondary"
                href={SITE_DISCORD}
                target="_blank"
                rel="noreferrer"
              >
                <img src={discordIconSVG} alt="" className="icon" />
                Join Discord
              </a>
            </div>
          </div>
        </div>
      </div>

      <ReasonFooter />
    </div>
  );
}

export default DownloadPage;
